import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Pencil, PiggyBank } from 'lucide-react'
import { fetchBudget, type BudgetPeriodType } from '@/api/budget'
import { useHousehold } from '@/hooks/useHousehold'
import { useIsReadOnly } from '@/hooks/useIsReadOnly'
import { BudgetPeriodControls } from '@/components/BudgetPeriodControls'
import { BudgetHero } from '@/components/budget/BudgetHero'
import { EditBudgetDialog } from '@/components/budget/EditBudgetDialog'
import { MonthlyBudgets } from '@/components/budget/MonthlyBudgets'
import { YearlyBudgets } from '@/components/budget/YearlyBudgets'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import { periodProgress } from '@/lib/budgetPace'
import {
  convertPeriod,
  currentPeriodValue,
  formatPeriodLabel,
  periodBounds,
  periodTransactionFilter,
} from '@/lib/budgetPeriod'
import { formatMonthName } from '@/lib/formatDate'
import { transactionsLink } from '@/lib/transactionsLink'
import { ROUTES } from '@/routes/routes'

function monthsOf(start: Date, end: Date): Set<string> {
  const months = new Set<string>()
  for (let d = new Date(start); d <= end; d = new Date(d.getFullYear(), d.getMonth() + 1, 1)) {
    months.add(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`)
  }
  return months
}

export default function BudgetPage() {
  const isReadOnly = useIsReadOnly()
  const { data: household } = useHousehold()
  const fy = household?.financialYearStartMonth ?? 4
  const [periodType, setPeriodType] = useState<BudgetPeriodType>('MONTHLY')
  const [period, setPeriod] = useState(() => currentPeriodValue('MONTHLY', fy))
  const [editing, setEditing] = useState(false)

  function handlePeriodTypeChange(nextType: BudgetPeriodType) {
    setPeriod((current) => convertPeriod(periodType, current, nextType, fy))
    setPeriodType(nextType)
  }

  const { data, isLoading, isError } = useQuery({
    queryKey: ['budget', periodType, period, fy],
    queryFn: () => fetchBudget({ periodType, period }),
  })

  const periodLabel = formatPeriodLabel(periodType, period, fy)
  const progress = periodProgress(periodType, period, fy, new Date())
  const { start, end } = periodBounds(periodType, period, fy)
  const periodFilter = periodTransactionFilter(periodType, period, fy)
  const back = { back: ROUTES.APP.BUDGET }
  const yearly = data?.yearly?.categories.length ? data.yearly : null
  const noSettings =
    data && data.spending.budgetLimit == null && data.categories.length === 0 && !yearly

  return (
    <div className="space-y-4 p-4 md:space-y-5 md:p-10">
      <header className="flex flex-wrap items-center gap-3">
        <h1 className="mr-auto font-serif text-[22px] font-semibold text-[var(--fl-heading)] md:text-[30px]">
          Budget
        </h1>
        {/* On a phone the period controls drop below the title row; the Edit button stays beside the title. */}
        <div className="order-last w-full md:order-none md:w-auto">
          <BudgetPeriodControls
            fyStartMonth={fy}
            disabled={!household}
            periodType={periodType}
            period={period}
            onPeriodTypeChange={handlePeriodTypeChange}
            onPeriodChange={setPeriod}
          />
        </div>
        {!isReadOnly && (
          <Button
            type="button"
            variant="outline"
            onClick={() => setEditing(true)}
            className="h-auto gap-1.5 rounded-lg px-3 py-2 text-sm font-medium"
          >
            <Pencil size={14} /> Edit budget
          </Button>
        )}
      </header>

      {isLoading && (
        <div className="space-y-4" aria-hidden="true">
          <div className="h-[132px] animate-pulse rounded-2xl bg-[var(--fl-surface-subtle)]" />
          <div className="h-[240px] animate-pulse rounded-2xl bg-[var(--fl-surface-subtle)]" />
        </div>
      )}

      {isError && (
        <div className="rounded-2xl border border-[var(--fl-border)] bg-[var(--fl-surface)] p-6 text-sm text-[var(--fl-error)]">
          Failed to load budget data.
        </div>
      )}

      {data && noSettings && (
        <EmptyState
          icon={PiggyBank}
          title="No budget yet"
          description="Set a monthly spending budget, and a monthly or yearly budget for any spending category, to see how you're doing."
          action={
            isReadOnly ? undefined : { label: 'Set up budget', onClick: () => setEditing(true) }
          }
        />
      )}

      {data && !noSettings && (
        <>
          <BudgetHero data={data} periodLabel={periodLabel} progress={progress} />
          <div className={yearly ? 'grid items-start gap-4 lg:grid-cols-2 lg:gap-5' : undefined}>
            <MonthlyBudgets
              categories={data.categories}
              unbudgetedSpend={data.unbudgetedSpend}
              heading={periodType === 'MONTHLY' ? formatMonthName(period) : periodLabel}
              progress={progress}
              hrefFor={(categoryId) =>
                transactionsLink({ ...periodFilter, categoryId, type: 'EXPENSE' }, back)
              }
              unbudgetedHref={transactionsLink({ ...periodFilter, type: 'EXPENSE' }, back)}
            />
            {yearly && (
              <YearlyBudgets
                yearly={yearly}
                periodType={periodType}
                periodMonths={monthsOf(start, end)}
                yearHrefFor={(categoryId) =>
                  transactionsLink(
                    { dateFrom: yearly.fyStart, dateTo: yearly.fyEnd, categoryId, type: 'EXPENSE' },
                    back
                  )
                }
              />
            )}
          </div>
        </>
      )}

      {editing && <EditBudgetDialog onClose={() => setEditing(false)} />}
    </div>
  )
}
